/**
 * MCP Tools
 * Define tools that AI agents can execute
 */

import { type Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  completeTodoItem,
  createTodoItem,
  updateTodoItem,
  deleteTodoItem,
  getTodoList,
  acceptPendingChange,
  rejectPendingChange,
  addSubtask,
  completeSubtask,
  updateSubtask,
  deleteSubtask,
  getSubtaskProgress,
} from '../api/todo.js';
import { detectProject, getProjectId } from '../utils/project.js';
import { getSystemTools, executeSystemTool } from './system.js';
import { getCliAgentToolDefinitions, executeCliAgentTool } from './cli-agent-tools.js';
import { getCodingToolDefinitions, executeCodingTool, CODING_TOOL_NAMES } from './coding-tools.js';
import { getWorksToolDefinitions, executeWorksTool, WORKS_TOOL_NAMES } from './works-tools.js';
import { getWalletToolDefinitions, executeWalletTool, WALLET_TOOL_NAMES } from './wallet-tools.js';
import { getLoopToolDefinitions, executeLoopTool, LOOP_TOOL_NAMES } from './loop.js';
import { getContextToolDefinitions, executeContextTool, CONTEXT_TOOL_NAMES } from './context-tools.js';
import {
  getCreditsStatus,
  getDepositInfo,
  pollDeposits,
  getDepositStatus,
} from '../api/credits.js';

/** System tool names that must be routed to executeSystemTool */
const SYSTEM_TOOL_NAMES = new Set([
  'paean_execute_shell',
  'paean_check_process',
  'paean_kill_process',
  'paean_download_file',
  'paean_write_file',
  'paean_read_file',
  'paean_list_directory',
  'paean_wechat_send',
  'paean_wechat_contacts',
]);
const SYSTEM_TOOL_PREFIXES: string[] = [];

/** CLI agent tool names */
const CLI_AGENT_TOOL_NAMES = new Set([
  'paean_list_cli_agents',
  'paean_check_cli_agent',
  'paean_invoke_cli_agent',
  'paean_set_cli_agent_enabled',
  'paean_setup_a8e',
]);

/**
 * Get available MCP tools (including system tools)
 */
export function getMcpTools(): Tool[] {
  return [
    // System/Shell tools
    ...getSystemTools(),
    // Local coding tools (edit, grep, glob, read-many, web-fetch, memory)
    ...getCodingToolDefinitions(),
    // CLI Agent tools
    ...getCliAgentToolDefinitions(),
    // 0works marketplace tools
    ...getWorksToolDefinitions(),
    // Wallet authentication tools
    ...getWalletToolDefinitions(),
    // Session-scoped loop tools (local recurring tasks)
    ...getLoopToolDefinitions(),
    // Context management tools (clear/compact)
    ...getContextToolDefinitions(),
    // Task management tools
    {
      name: 'paean_complete_task',
      description:
        'Mark a task as completed. Call this after successfully completing a task.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The ID of the task to complete',
          },
          summary: {
            type: 'string',
            description: 'A brief summary of what was done to complete the task',
          },
        },
        required: ['taskId'],
      },
    },
    {
      name: 'paean_create_task',
      description:
        'Create a new task. Use this when you discover additional work that needs to be done. Supports creating tasks with subtasks for complex multi-step work.',
      inputSchema: {
        type: 'object',
        properties: {
          content: {
            type: 'string',
            description: 'The task description/title',
          },
          description: {
            type: 'string',
            description: 'Additional notes or details about the task',
          },
          priority: {
            type: 'string',
            enum: ['high', 'medium', 'low'],
            description: 'Task priority level',
          },
          tags: {
            type: 'array',
            items: { type: 'string' },
            description: 'Optional tags to categorize the task',
          },
          sourceContext: {
            type: 'string',
            description: 'Context about why this task was created',
          },
          subtasks: {
            type: 'array',
            items: { type: 'string' },
            description: 'List of subtask contents. Use this to break down complex tasks into smaller steps.',
          },
        },
        required: ['content'],
      },
    },
    {
      name: 'paean_update_task',
      description: 'Update an existing task (status, priority, or content)',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The ID of the task to update',
          },
          status: {
            type: 'string',
            enum: ['pending', 'in_progress'],
            description: 'New task status',
          },
          priority: {
            type: 'string',
            enum: ['high', 'medium', 'low'],
            description: 'New task priority',
          },
          content: {
            type: 'string',
            description: 'Updated task content',
          },
        },
        required: ['taskId'],
      },
    },
    {
      name: 'paean_list_tasks',
      description:
        'List tasks with optional filtering, search, and pagination. ' +
        'Supports fuzzy search on title/content, tag-based filtering, and cursor-based pagination for large task lists.',
      inputSchema: {
        type: 'object',
        properties: {
          status: {
            type: 'string',
            enum: ['all', 'pending', 'in_progress', 'completed'],
            description: 'Filter by task status',
          },
          priority: {
            type: 'string',
            enum: ['high', 'medium', 'low'],
            description: 'Filter by priority',
          },
          search: {
            type: 'string',
            description: 'Fuzzy search on task title/content (case-insensitive)',
          },
          tags: {
            type: 'array',
            items: { type: 'string' },
            description: 'Filter tasks by tags (returns tasks matching ANY of the given tags)',
          },
          limit: {
            type: 'number',
            description: 'Max number of tasks to return (default: 50, max: 200)',
          },
          offset: {
            type: 'number',
            description: 'Number of tasks to skip for pagination (default: 0)',
          },
        },
      },
    },
    {
      name: 'paean_batch_complete_tasks',
      description:
        'Mark multiple tasks as completed in a single operation. ' +
        'Much more efficient than calling paean_complete_task repeatedly. ' +
        'Returns results for each task (success or failure).',
      inputSchema: {
        type: 'object',
        properties: {
          taskIds: {
            type: 'array',
            items: { type: 'string' },
            description: 'Array of task IDs to complete',
          },
          summary: {
            type: 'string',
            description: 'A shared summary applied to all completed tasks',
          },
        },
        required: ['taskIds'],
      },
    },
    {
      name: 'paean_batch_update_tasks',
      description:
        'Update multiple tasks with the same changes in a single operation. ' +
        'Useful for bulk status changes, priority adjustments, or tagging.',
      inputSchema: {
        type: 'object',
        properties: {
          taskIds: {
            type: 'array',
            items: { type: 'string' },
            description: 'Array of task IDs to update',
          },
          status: {
            type: 'string',
            enum: ['pending', 'in_progress'],
            description: 'New status to apply to all tasks',
          },
          priority: {
            type: 'string',
            enum: ['high', 'medium', 'low'],
            description: 'New priority to apply to all tasks',
          },
          tags: {
            type: 'array',
            items: { type: 'string' },
            description: 'Tags to set on all tasks',
          },
        },
        required: ['taskIds'],
      },
    },
    {
      name: 'paean_delete_task',
      description:
        'Permanently delete a task. Use this for tasks that are no longer relevant or were created by mistake. ' +
        'For tasks that are done, prefer paean_complete_task instead.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The ID of the task to delete',
          },
        },
        required: ['taskId'],
      },
    },
    {
      name: 'paean_accept_change',
      description: 'Accept a pending AI-suggested change',
      inputSchema: {
        type: 'object',
        properties: {
          changeId: {
            type: 'number',
            description: 'The ID of the pending change to accept',
          },
        },
        required: ['changeId'],
      },
    },
    {
      name: 'paean_reject_change',
      description: 'Reject a pending AI-suggested change',
      inputSchema: {
        type: 'object',
        properties: {
          changeId: {
            type: 'number',
            description: 'The ID of the pending change to reject',
          },
          reason: {
            type: 'string',
            description: 'Reason for rejecting the change',
          },
        },
        required: ['changeId'],
      },
    },
    // Subtask management tools
    {
      name: 'paean_add_subtask',
      description: 'Add a subtask (checklist item) to an existing task. Use this to break down a task into smaller steps.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The ID of the parent task to add the subtask to',
          },
          content: {
            type: 'string',
            description: 'The subtask description',
          },
        },
        required: ['taskId', 'content'],
      },
    },
    {
      name: 'paean_complete_subtask',
      description: 'Mark a subtask as completed. Use this when a specific subtask/checklist item is done.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The ID of the parent task',
          },
          subtaskId: {
            type: 'string',
            description: 'The ID of the subtask to complete',
          },
        },
        required: ['taskId', 'subtaskId'],
      },
    },
    {
      name: 'paean_update_subtask',
      description: 'Update a subtask content or completion status.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The ID of the parent task',
          },
          subtaskId: {
            type: 'string',
            description: 'The ID of the subtask to update',
          },
          content: {
            type: 'string',
            description: 'New content for the subtask',
          },
          completed: {
            type: 'boolean',
            description: 'Set completion status (true=done, false=not done)',
          },
        },
        required: ['taskId', 'subtaskId'],
      },
    },
    {
      name: 'paean_delete_subtask',
      description: 'Delete a subtask from a task.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The ID of the parent task',
          },
          subtaskId: {
            type: 'string',
            description: 'The ID of the subtask to delete',
          },
        },
        required: ['taskId', 'subtaskId'],
      },
    },
    // Credits & Deposit tools
    {
      name: 'paean_check_credits',
      description:
        'Check the current Paean AI credits balance, subscription tier, and recovery status. ' +
        'Use this to determine if the user needs to top up credits. ' +
        'Returns credits count, total capacity, tier, and next recovery time. ' +
        'If lowCredits is true, suggest using paean_get_deposit_info to get USDC deposit addresses.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'paean_get_deposit_info',
      description:
        'Get the user\'s unique USDC deposit addresses for topping up Paean AI credits. ' +
        'Returns one address per supported chain (Base, Solana). ' +
        'Conversion rate: 1 USDC = 100 credits. Minimum deposit: 1 USDC (0.01 USDC for testing). ' +
        'After getting the address, use an external payment tool (e.g. send_usdc) to send USDC to the deposit address.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'paean_poll_deposits',
      description:
        'Trigger a blockchain scan for incoming USDC deposits and return current deposit status. ' +
        'Returns all pending and recently confirmed deposits, plus the current credit balance. ' +
        'Call this after sending USDC to verify the deposit was detected and credits were awarded.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'paean_get_deposit_status',
      description:
        'Check the status of a specific USDC deposit by transaction hash. ' +
        'Returns confirmation count, credits awarded, and explorer URL. ' +
        'Use this to track a specific transaction after sending USDC.',
      inputSchema: {
        type: 'object',
        properties: {
          txHash: {
            type: 'string',
            description: 'The blockchain transaction hash to look up',
          },
        },
        required: ['txHash'],
      },
    },
  ];
}

/**
 * Execute an MCP tool
 */
export async function executeMcpTool(
  toolName: string,
  args: Record<string, unknown>
): Promise<unknown> {
  const project = detectProject();
  const projectId = getProjectId(project);

  switch (toolName) {
    case 'paean_complete_task': {
      const taskId = args.taskId as string;
      const summary = args.summary as string | undefined;

      if (!taskId) {
        return {
          success: false,
          error: 'taskId is required',
        };
      }

      try {
        const result = await completeTodoItem(taskId, summary);
        return {
          success: true,
          message: 'Task completed successfully',
          task: {
            id: result.data.id,
            content: result.data.content,
            status: result.data.status,
          },
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to complete task',
        };
      }
    }

    case 'paean_create_task': {
      const content = args.content as string;
      const description = args.description as string | undefined;
      const priority = (args.priority as 'high' | 'medium' | 'low') || 'medium';
      const tags = args.tags as string[] | undefined;
      const sourceContext = args.sourceContext as string | undefined;
      const subtasks = args.subtasks as string[] | undefined;

      if (!content) {
        return {
          success: false,
          error: 'content is required',
        };
      }

      try {
        const result = await createTodoItem({
          content,
          description,
          priority,
          tags,
          sourceContext,
          subtasks,
        });

        const subtaskInfo = subtasks?.length
          ? ` with ${subtasks.length} subtask(s)`
          : '';

        return {
          success: true,
          message: `Task created successfully${subtaskInfo}`,
          task: {
            id: result.data.id,
            content: result.data.content,
            description: result.data.description,
            priority: result.data.priority,
            status: result.data.status,
            subtaskCount: result.data.checklist?.length || 0,
          },
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to create task',
        };
      }
    }

    case 'paean_update_task': {
      const taskId = args.taskId as string;
      const updates: Record<string, unknown> = {};

      if (args.status) updates.status = args.status;
      if (args.priority) updates.priority = args.priority;
      if (args.content) updates.content = args.content;

      if (!taskId) {
        return {
          success: false,
          error: 'taskId is required',
        };
      }

      if (Object.keys(updates).length === 0) {
        return {
          success: false,
          error: 'At least one update field is required (status, priority, or content)',
        };
      }

      try {
        const result = await updateTodoItem(taskId, updates);
        return {
          success: true,
          message: 'Task updated successfully',
          task: {
            id: result.data.id,
            content: result.data.content,
            priority: result.data.priority,
            status: result.data.status,
          },
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to update task',
        };
      }
    }

    case 'paean_list_tasks': {
      const status = args.status as string | undefined;
      const priority = args.priority as string | undefined;
      const search = args.search as string | undefined;
      const tags = args.tags as string[] | undefined;
      const limit = Math.min(Math.max((args.limit as number) || 50, 1), 200);
      const offset = Math.max((args.offset as number) || 0, 0);

      try {
        const result = await getTodoList({
          status: status as 'all' | 'pending' | 'completed' | 'in_progress' | 'cancelled' | undefined,
          priority: priority as 'high' | 'medium' | 'low' | undefined,
        });

        let items = result.data.items;

        // Apply search filter (case-insensitive fuzzy match on content + description)
        if (search) {
          const searchLower = search.toLowerCase();
          items = items.filter((t) =>
            t.content.toLowerCase().includes(searchLower) ||
            (t.description && t.description.toLowerCase().includes(searchLower))
          );
        }

        // Apply tags filter (match ANY of the given tags)
        if (tags && tags.length > 0) {
          const tagsLower = tags.map(t => t.toLowerCase());
          items = items.filter((t) =>
            t.tags && t.tags.some(tag => tagsLower.includes(tag.toLowerCase()))
          );
        }

        const totalFiltered = items.length;

        // Apply pagination
        items = items.slice(offset, offset + limit);

        return {
          success: true,
          tasks: items.map((t) => {
            const progress = getSubtaskProgress(t);
            return {
              id: t.id,
              content: t.content,
              description: t.description,
              priority: t.priority,
              status: t.status,
              tags: t.tags,
              dueDate: t.dueDate,
              createdAt: t.createdAt,
              // Subtask information
              hasSubtasks: progress.total > 0,
              subtaskCount: progress.total,
              subtasksCompleted: progress.completed,
              subtaskProgress: progress.percentage,
              subtasks: t.checklist?.map(s => ({
                id: s.id,
                content: s.content,
                completed: s.completed,
              })) || [],
            };
          }),
          pagination: {
            total: totalFiltered,
            offset,
            limit,
            hasMore: offset + limit < totalFiltered,
          },
          stats: result.data.stats,
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to list tasks',
        };
      }
    }

    case 'paean_batch_complete_tasks': {
      const taskIds = args.taskIds as string[];
      const summary = args.summary as string | undefined;

      if (!taskIds || !Array.isArray(taskIds) || taskIds.length === 0) {
        return {
          success: false,
          error: 'taskIds array is required and must not be empty',
        };
      }

      if (taskIds.length > 50) {
        return {
          success: false,
          error: 'Maximum 50 tasks can be completed in a single batch',
        };
      }

      const results: Array<{ taskId: string; success: boolean; error?: string }> = [];
      let successCount = 0;

      // Process in parallel with concurrency limit
      const batchSize = 5;
      for (let i = 0; i < taskIds.length; i += batchSize) {
        const batch = taskIds.slice(i, i + batchSize);
        const batchResults = await Promise.allSettled(
          batch.map(async (taskId) => {
            try {
              await completeTodoItem(taskId, summary);
              return { taskId, success: true };
            } catch (error) {
              return {
                taskId,
                success: false,
                error: error instanceof Error ? error.message : 'Failed to complete',
              };
            }
          })
        );

        for (const result of batchResults) {
          if (result.status === 'fulfilled') {
            results.push(result.value);
            if (result.value.success) successCount++;
          } else {
            results.push({ taskId: 'unknown', success: false, error: 'Unexpected error' });
          }
        }
      }

      return {
        success: successCount > 0,
        message: `Completed ${successCount}/${taskIds.length} tasks`,
        results,
        summary: {
          total: taskIds.length,
          succeeded: successCount,
          failed: taskIds.length - successCount,
        },
        project: project.name,
        projectId,
      };
    }

    case 'paean_batch_update_tasks': {
      const taskIds = args.taskIds as string[];
      const status = args.status as string | undefined;
      const priority = args.priority as string | undefined;
      const tags = args.tags as string[] | undefined;

      if (!taskIds || !Array.isArray(taskIds) || taskIds.length === 0) {
        return {
          success: false,
          error: 'taskIds array is required and must not be empty',
        };
      }

      if (taskIds.length > 50) {
        return {
          success: false,
          error: 'Maximum 50 tasks can be updated in a single batch',
        };
      }

      const updates: Record<string, unknown> = {};
      if (status) updates.status = status;
      if (priority) updates.priority = priority;
      if (tags) updates.tags = tags;

      if (Object.keys(updates).length === 0) {
        return {
          success: false,
          error: 'At least one update field is required (status, priority, or tags)',
        };
      }

      const results: Array<{ taskId: string; success: boolean; error?: string }> = [];
      let successCount = 0;

      const batchSize = 5;
      for (let i = 0; i < taskIds.length; i += batchSize) {
        const batch = taskIds.slice(i, i + batchSize);
        const batchResults = await Promise.allSettled(
          batch.map(async (taskId) => {
            try {
              await updateTodoItem(taskId, updates);
              return { taskId, success: true };
            } catch (error) {
              return {
                taskId,
                success: false,
                error: error instanceof Error ? error.message : 'Failed to update',
              };
            }
          })
        );

        for (const result of batchResults) {
          if (result.status === 'fulfilled') {
            results.push(result.value);
            if (result.value.success) successCount++;
          } else {
            results.push({ taskId: 'unknown', success: false, error: 'Unexpected error' });
          }
        }
      }

      return {
        success: successCount > 0,
        message: `Updated ${successCount}/${taskIds.length} tasks`,
        updates,
        results,
        summary: {
          total: taskIds.length,
          succeeded: successCount,
          failed: taskIds.length - successCount,
        },
        project: project.name,
        projectId,
      };
    }

    case 'paean_delete_task': {
      const taskId = args.taskId as string;

      if (!taskId) {
        return {
          success: false,
          error: 'taskId is required',
        };
      }

      try {
        await deleteTodoItem(taskId);
        return {
          success: true,
          message: 'Task deleted successfully',
          taskId,
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to delete task',
        };
      }
    }

    case 'paean_accept_change': {
      const changeId = args.changeId as number;

      if (!changeId) {
        return {
          success: false,
          error: 'changeId is required',
        };
      }

      try {
        await acceptPendingChange(changeId);
        return {
          success: true,
          message: 'Change accepted successfully',
          changeId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to accept change',
        };
      }
    }

    case 'paean_reject_change': {
      const changeId = args.changeId as number;
      const reason = args.reason as string | undefined;

      if (!changeId) {
        return {
          success: false,
          error: 'changeId is required',
        };
      }

      try {
        await rejectPendingChange(changeId, reason);
        return {
          success: true,
          message: 'Change rejected successfully',
          changeId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to reject change',
        };
      }
    }

    // Subtask management tools
    case 'paean_add_subtask': {
      const taskId = args.taskId as string;
      const content = args.content as string;

      if (!taskId || !content) {
        return {
          success: false,
          error: 'taskId and content are required',
        };
      }

      try {
        const result = await addSubtask(taskId, content);
        return {
          success: true,
          message: 'Subtask added successfully',
          taskId,
          subtaskId: result.subtaskId,
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to add subtask',
        };
      }
    }

    case 'paean_complete_subtask': {
      const taskId = args.taskId as string;
      const subtaskId = args.subtaskId as string;

      if (!taskId || !subtaskId) {
        return {
          success: false,
          error: 'taskId and subtaskId are required',
        };
      }

      try {
        const result = await completeSubtask(taskId, subtaskId);
        const progress = getSubtaskProgress(result.data);

        return {
          success: true,
          message: `Subtask completed! Progress: ${progress.completed}/${progress.total} (${progress.percentage}%)`,
          taskId,
          subtaskId,
          progress: {
            completed: progress.completed,
            total: progress.total,
            percentage: progress.percentage,
          },
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to complete subtask',
        };
      }
    }

    case 'paean_update_subtask': {
      const taskId = args.taskId as string;
      const subtaskId = args.subtaskId as string;
      const content = args.content as string | undefined;
      const completed = args.completed as boolean | undefined;

      if (!taskId || !subtaskId) {
        return {
          success: false,
          error: 'taskId and subtaskId are required',
        };
      }

      if (content === undefined && completed === undefined) {
        return {
          success: false,
          error: 'At least one of content or completed must be provided',
        };
      }

      try {
        const updates: { content?: string; completed?: boolean } = {};
        if (content !== undefined) updates.content = content;
        if (completed !== undefined) updates.completed = completed;

        await updateSubtask(taskId, subtaskId, updates);
        return {
          success: true,
          message: 'Subtask updated successfully',
          taskId,
          subtaskId,
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to update subtask',
        };
      }
    }

    case 'paean_delete_subtask': {
      const taskId = args.taskId as string;
      const subtaskId = args.subtaskId as string;

      if (!taskId || !subtaskId) {
        return {
          success: false,
          error: 'taskId and subtaskId are required',
        };
      }

      try {
        await deleteSubtask(taskId, subtaskId);
        return {
          success: true,
          message: 'Subtask deleted successfully',
          taskId,
          subtaskId,
          project: project.name,
          projectId,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to delete subtask',
        };
      }
    }

    // Credits & Deposit tools
    case 'paean_check_credits': {
      try {
        const response = await getCreditsStatus();
        const d = response.data;
        const isLow = d.credits < 20;
        const result: Record<string, unknown> = {
          success: true,
          credits: d.credits,
          totalCredits: d.totalCredits,
          subscriptionTier: d.subscriptionTier,
          canRecover: d.canRecover,
          nextRecoveryAt: d.nextRecoveryAt,
          recoveryIntervalHours: d.recoveryIntervalHours,
          lowCredits: isLow,
        };
        if (isLow) {
          result.hint = 'Credits are low. Call paean_get_deposit_info to get USDC deposit addresses for top-up (1 USDC = 100 credits).';
        }
        return result;
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to check credits',
        };
      }
    }

    case 'paean_get_deposit_info': {
      try {
        const info = await getDepositInfo();
        return {
          success: true,
          networks: info.networks,
          conversionRate: info.conversionRate,
          conversionRateNumeric: info.conversionRateNumeric,
          minimumDeposit: info.minimumDeposit,
          testMinimumDeposit: info.testMinimumDeposit,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to get deposit info',
        };
      }
    }

    case 'paean_poll_deposits': {
      try {
        const result = await pollDeposits();
        return {
          success: true,
          credits: result.credits,
          deposits: result.deposits,
          pendingCount: result.deposits.filter((d: any) => d.status === 'pending').length,
          confirmedCount: result.deposits.filter((d: any) => d.status === 'confirmed').length,
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to poll deposits',
        };
      }
    }

    case 'paean_get_deposit_status': {
      const txHash = args.txHash as string;
      if (!txHash) {
        return { success: false, error: 'txHash is required' };
      }
      try {
        const result = await getDepositStatus(txHash);
        return { success: true, ...result };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to get deposit status',
        };
      }
    }

    default: {
      // Check if it's a system tool (shell, filesystem, worker)
      if (SYSTEM_TOOL_NAMES.has(toolName) ||
          SYSTEM_TOOL_PREFIXES.some(p => toolName.startsWith(p))) {
        return executeSystemTool(toolName, args);
      }
      // Check if it's a coding tool (edit, grep, glob, read-many, web-fetch, memory)
      if (CODING_TOOL_NAMES.has(toolName)) {
        return executeCodingTool(toolName, args);
      }
      // Check if it's a CLI agent tool
      if (CLI_AGENT_TOOL_NAMES.has(toolName)) {
        return executeCliAgentTool(toolName, args);
      }
      // Check if it's a 0works marketplace tool
      if (WORKS_TOOL_NAMES.has(toolName)) {
        return executeWorksTool(toolName, args);
      }
      // Check if it's a wallet tool
      if (WALLET_TOOL_NAMES.has(toolName)) {
        return executeWalletTool(toolName, args);
      }
      // Check if it's a loop tool (local recurring tasks)
      if (LOOP_TOOL_NAMES.has(toolName)) {
        return executeLoopTool(toolName, args);
      }
      // Check if it's a context management tool (clear/compact)
      if (CONTEXT_TOOL_NAMES.has(toolName)) {
        return executeContextTool(toolName, args);
      }
      return {
        success: false,
        error: `Unknown tool: ${toolName}`,
      };
    }
  }
}
